<script setup lang="ts" generic="SortField extends string, AdvancedFilters extends object">
import { computed, ref, useSlots, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDebounceFn } from '@vueuse/core'
import { BaseGrid, BaseGridCol, BaseInputText, BaseIconButton, BaseSortButton } from '@cplug/design-system/components'
import {
  AlignItems,
  BaseIconName,
  BaseIconColor,
  BaseModalType,
  BaseModalWidthSize,
  BaseButtonType,
  SortOrder
} from '@cplug/design-system/components/props'
import { type Filters, OrderFilterKey, useFormModalStore } from '@cplug/frontend-core/stores'
import { type BaseIndexFilterProps } from './BaseIndexFilterProps'
import { useDataTestid, DATA_TESTID_SUBDOMAINS, DATA_TESTID_ELEMENTS } from '~/shared/composables/useDataTestid'

const filters = defineModel<Filters>({ required: true })

const emit = defineEmits(['clearAdvancedFilters'])

const props = withDefaults(defineProps<BaseIndexFilterProps<SortField, AdvancedFilters>>(), {
  searchOnIconClick: false
})

const { t } = useI18n()
const slots = useSlots()

const formModalStore = useFormModalStore<AdvancedFilters>()

// Get initial search field value
const search = ref<string>(String(filters.value[props.searchField ?? ''] ?? ''))
const advancedFilters = computed<AdvancedFilters>(() => props?.advancedFilters ?? ({} as AdvancedFilters))

const sortField = ref<string>(filters.value[OrderFilterKey.ByField]!)
const sortOrder = ref<SortOrder>(filters.value[OrderFilterKey.ByType]!)

const hasSearchField = computed<boolean>(() => (props.searchField?.length ?? 0) > 0)
const hasSortFields = computed<boolean>(() => (props.sortFields?.length ?? 0) > 0)
const hasAdvancedFilters = computed<boolean>(() => props.advancedFiltersStore != null)

const hasRightSideContent = computed(() => hasSearchField.value || hasSortFields.value || hasAdvancedFilters.value)
const hasLeftSide = computed(() => !!slots.leftSide)
const gridAlignItems = computed(() => (
  hasLeftSide.value ? AlignItems.End : AlignItems.Stretch
))
const rightColumnClass = computed(() => (
  hasLeftSide.value
    ? 'flex items-end gap-2 justify-end'
    : 'flex items-center gap-2 justify-end'
))

if (hasSortFields.value && (!sortField.value || !sortOrder.value)) {
  throw new Error('sortField and sortOrder is required in filter')
}

function clearAdvancedFilters (): void {
  emit('clearAdvancedFilters')
  formModalStore.close()
}

function onClickAdvancedFiltersTrigger (): void {
  formModalStore.open({
    type: BaseModalType.Lateral,
    content: props.advancedFiltersForm!,
    contentProps: props.advancedFiltersFormProps,
    model: advancedFilters.value,
    modelStoreInstance: props.advancedFiltersStore!,
    actionButton: {
      title: t('Clean'),
      type: BaseButtonType.Destructive,
      action: clearAdvancedFilters
    },
    primaryButton: {
      title: t('ApplyFilters'),
      type: BaseButtonType.Primary
    },
    secondaryButton: { hide: true },
    title: t('Filter'),
    widthSize: props.advancedFiltersModalWidthSize ?? BaseModalWidthSize.Small,
    tabs: props.tabs,
    defaultButtonBehaviourOnTabs: props.defaultButtonBehaviourOnTabs ?? false
  })
}

function applyFilters (): void {
  let updatedFilters: Filters = {}

  if (hasSearchField.value) {
    updatedFilters = { ...updatedFilters, [props.searchField!]: search.value }

    if (search.value === '') {
      delete updatedFilters[props.searchField!]
      delete filters.value[props.searchField!]
    }
  }

  if (hasSortFields.value) {
    updatedFilters = {
      ...updatedFilters,
      [OrderFilterKey.ByField]: sortField.value,
      [OrderFilterKey.ByType]: sortOrder.value
    }
  }

  if (hasAdvancedFilters.value) {
    updatedFilters = {
      ...updatedFilters,
      ...(props.advancedFiltersMapperToDto
        ? props.advancedFiltersMapperToDto(advancedFilters.value)
        : advancedFilters.value)
    }
  }

  filters.value = { ...filters.value, ...updatedFilters }
}

function handleSearchIconClick (): void {
  if (props.searchOnIconClick) {
    applyFilters()
  }
}

function handleSearchKeydown (event: KeyboardEvent): void {
  if (props.searchOnIconClick && event.key === 'Enter') {
    applyFilters()
  }
}

const debounceSearch = useDebounceFn(applyFilters, 500)

// Watch search only if searchOnIconClick is false (default behavior)
if (!props.searchOnIconClick) {
  watch([search, sortField, sortOrder], () => {
    debounceSearch()
  })
} else {
  // When searchOnIconClick is true, only watch sort fields
  watch([sortField, sortOrder], () => {
    applyFilters()
  })
}

watch(
  [advancedFilters],
  () => {
    applyFilters()
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <BaseGrid class="pb-6 lg:px-0 px-4" :align-items="gridAlignItems">
    <BaseGridCol :cols="12" :lg="6">
      <slot name="leftSide" />
    </BaseGridCol>

    <BaseGridCol :cols="hasRightSideContent ? 12 : 0" :lg="6" :class="rightColumnClass">
      <BaseInputText
        v-if="hasSearchField"
        v-model="search"
        :placeholder="props.searchPlaceholder"
        :icon-suffix="BaseIconName.Search"
        :icon-suffix-color="BaseIconColor.Interactive"
        :data-testid="useDataTestid(DATA_TESTID_SUBDOMAINS.INDEX_FILTER, 'search', DATA_TESTID_ELEMENTS.INPUT)"
        @click:icon-suffix="handleSearchIconClick"
        @keydown="handleSearchKeydown"
      />
      <slot name="afterSearch" />
      <BaseIconButton
        v-if="hasAdvancedFilters"
        :pip-badge="Object.keys(advancedFilters).length > 0"
        :contained="false"
        :icon="BaseIconName.Filter"
        :data-testid="useDataTestid(DATA_TESTID_SUBDOMAINS.INDEX_FILTER, 'advanced-filters', DATA_TESTID_ELEMENTS.BUTTON)"
        @click="onClickAdvancedFiltersTrigger"
      />
      <BaseSortButton
        v-if="hasSortFields"
        v-model="sortField"
        v-model:sort-order="sortOrder"
        :sort-fields="sortFields!"
        :data-testid="useDataTestid(DATA_TESTID_SUBDOMAINS.INDEX_FILTER, 'sort', DATA_TESTID_ELEMENTS.BUTTON)"
      />
    </BaseGridCol>
  </BaseGrid>
</template>
<i18n lang="yaml">
pt-br:
  ApplyFilters: 'Aplicar filtros'
  Clean: 'Limpar'
  Filter: 'Filtrar'
</i18n>
